import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type Prefs = {
  onFlag: boolean;
  onTakedownSucceeded: boolean;
  onTakedownFailed: boolean;
  onAuditComplete: boolean;
  weeklyDigest: boolean;
};

const DEFAULTS: Prefs = {
  onFlag: true,
  onTakedownSucceeded: true,
  onTakedownFailed: true,
  onAuditComplete: true,
  weeklyDigest: true,
};

const ROWS: Array<{ key: keyof Prefs; label: string; help: string }> = [
  {
    key: "onFlag",
    label: "New flagged review",
    help: "Email me when we flag a review on one of my brands.",
  },
  {
    key: "onTakedownSucceeded",
    label: "Takedown succeeded",
    help: "Email me when Amazon removes a review.",
  },
  {
    key: "onTakedownFailed",
    label: "Takedown declined",
    help: "Email me when Amazon declines a takedown request.",
  },
  {
    key: "onAuditComplete",
    label: "Audit complete",
    help: "Email me when a one-shot audit finishes scanning.",
  },
  {
    key: "weeklyDigest",
    label: "Weekly digest",
    help: "Summary of activity across all brands every Monday.",
  },
];

export default function DashboardNotifications() {
  return <AppShell>{() => <Inner />}</AppShell>;
}

function Inner() {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ prefs: Prefs }>("/customer/notifications").then((res) => {
      if (cancelled) return;
      setPrefs(res.data?.prefs ?? DEFAULTS);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(key: keyof Prefs) {
    setPrefs((cur) => (cur ? { ...cur, [key]: !cur[key] } : cur));
  }

  async function save() {
    if (!prefs) return;
    setError(null);
    setSaving(true);
    const res = await api<{ prefs: Prefs; savedAt: string }>(
      "/customer/notifications",
      { method: "PUT", body: JSON.stringify(prefs) }
    );
    setSaving(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Couldn't save preferences.");
      return;
    }
    setPrefs(res.data.prefs);
    setSavedAt(new Date(res.data.savedAt));
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Notifications
      </h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Choose which emails you want from Reputely.
      </p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {!prefs ? (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {ROWS.map((row) => (
              <li
                key={row.key}
                className="flex items-start justify-between gap-4 px-5 py-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {row.label}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {row.help}
                  </div>
                </div>
                <Toggle
                  checked={prefs[row.key]}
                  onChange={() => toggle(row.key)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!prefs || saving}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? "Saving…" : "Save preferences"}
        </button>
        {savedAt && !saving && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Saved {savedAt.toLocaleTimeString()}
          </span>
        )}
        {error && (
          <span className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" /> {error}
          </span>
        )}
      </div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={
        checked
          ? "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-emerald-500 transition"
          : "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-slate-300 transition dark:bg-slate-700"
      }
    >
      <span
        className={
          checked
            ? "inline-block h-4 w-4 translate-x-6 rounded-full bg-white shadow transition"
            : "inline-block h-4 w-4 translate-x-1 rounded-full bg-white shadow transition"
        }
      />
    </button>
  );
}
